import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
// import logo from "../assets/logo.png"; 
import {
  Loader2,
  LogOut,
  Check,
  ArrowRight,
} from 'lucide-react';
import {
  AccessBotOption,
  clearSelectedBot,
  getAccessOptions,
  selectBot,
  setSelectedBot,
} from './api';
import { getBotDisplayConfig } from './botConfig';
import './ProfileSelection.css';

function extractKeycloakRoles(keycloak: any) {
  const realmRoles = keycloak.tokenParsed?.realm_access?.roles || [];
  const clientRoles =
    keycloak.tokenParsed?.resource_access?.[keycloak.clientId]?.roles || [];

  return {
    realmRoles,
    clientRoles,
    mergedRoles: Array.from(new Set([...realmRoles, ...clientRoles])),
  };
}

function getInitials(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

function ProfileSelection() {
  const { keycloak, initialized } = useKeycloak();
  const navigate = useNavigate();
  const hasLoadedRef = useRef(false);

  const [options, setOptions] = useState<AccessBotOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const token = keycloak.token;
  const userName =
    keycloak.tokenParsed?.name ||
    keycloak.tokenParsed?.preferred_username ||
    'User';
  const email = keycloak.tokenParsed?.email || '';

  const handleLogout = () => {
    clearSelectedBot();
    keycloak.logout({ redirectUri: window.location.origin });
  };

  const openBot = useCallback(
    async (bot: AccessBotOption) => {
      if (!token) {
        setError('Authentication token is unavailable. Please reload the page.');
        return;
      }
      setError(null);
      setConfirming(true);
      try {
        await selectBot(token, bot.id);
        setSelectedBot(bot.id, bot.name);
        navigate('/chat', { replace: true });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Bot selection failed');
        setConfirming(false);
      }
    },
    [navigate, token]
  );

  useEffect(() => {
    if (!initialized || !keycloak.authenticated || !token || hasLoadedRef.current) {
      return;
    }

    hasLoadedRef.current = true;
    let active = true;

    const loadAccessOptions = async () => {
      try {
        const parsedRoles = extractKeycloakRoles(keycloak);
        console.log('[Auth] Keycloak role debug', {
          username:
            keycloak.tokenParsed?.preferred_username ||
            keycloak.tokenParsed?.email ||
            'unknown',
          realmRoles: parsedRoles.realmRoles,
          clientRoles: parsedRoles.clientRoles,
          mergedRoles: parsedRoles.mergedRoles,
          tokenParsed: keycloak.tokenParsed,
        });

        const data = await getAccessOptions(token);
        console.log('[Auth] Backend access-options response', {
          username: data.username,
          roles: data.roles,
          is_admin: data.is_admin,
          available_bots: data.available_bots,
          default_bot_id: data.default_bot_id,
        });

        if (!active) return;

        if (data.available_bots.length === 0) {
          clearSelectedBot();
          setError('No bot access is assigned to this account.');
          setLoading(false);
          return;
        }

        if (data.available_bots.length === 1) {
          setSelectedId(data.available_bots[0].id);
          setOptions(data.available_bots);
          setLoading(false);
          await openBot(data.available_bots[0]);
          return;
        }

        setOptions(data.available_bots);
        if (data.default_bot_id) {
          setSelectedId(data.default_bot_id);
        }
        setLoading(false);
      } catch (err) {
        if (!active) return;
        clearSelectedBot();
        setError(err instanceof Error ? err.message : 'Failed to load access options');
        setLoading(false);
      }
    };

    void loadAccessOptions();

    return () => {
      active = false;
    };
  }, [initialized, keycloak, keycloak.authenticated, token, openBot]);

  if (!initialized || loading) {
    return (
      <div className="hub-screen">
        <div className="hub-loading">
          <Loader2 className="hub-spinner" size={36} />
          <p>Loading access…</p>
        </div>
      </div>
    );
  }

  const selectedBot = options.find((o) => o.id === selectedId) || null;

  return (
    <div className="hub-screen">
      <header className="hub-topbar">
        <div className="hub-brand">
  <img
    src="/pv_logo.png"
    alt="Logo"
    className="hub-logo"
  />
  <div>
            <h4>FinPulse</h4>
            {/* <span>Digital.AI Assistant Hub</span> */}
          </div>
        </div>

        <div className="hub-user">
          <div className="hub-user-avatar">{getInitials(userName)}</div>
          <div className="hub-user-meta">
            <strong>{userName}</strong>
            <span>{email}</span>
          </div>
          <button className="hub-logout" onClick={handleLogout} aria-label="Logout">
            <LogOut size={16} />
          </button>
        </div>
      </header>

      <main className="hub-main">
        <div className="hub-heading">
          <h1>Choose an assistant</h1>
          <p>Select the knowledge assistant you'd like to work with this session.</p>
        </div>

        {error && <div className="hub-error">{error}</div>}

        <div className="hub-grid">
          {options.map((bot) => {
            const config = getBotDisplayConfig(bot.id, bot.name);
            const Icon = config.icon;
            const isSelected = selectedId === bot.id;

            return (
              <button
                key={bot.id}
                type="button"
                className={`hub-card ${config.themeClassName} ${isSelected ? 'is-selected' : ''}`}
                onClick={() => setSelectedId(bot.id)}
                disabled={confirming}
                aria-pressed={isSelected}
              >
                {isSelected && (
                  <span className="hub-check">
                    <Check size={13} strokeWidth={3} />
                  </span>
                )}

                <div className="hub-card-icon">
                  <Icon size={40} strokeWidth={1.5} />
                </div>

                <h3>{bot.name}</h3>
                <p>{bot.description}</p>
                <span className="hub-card-role">{bot.required_role}</span>
              </button>
            );
          })}
        </div>
      </main>

<footer className="hub-footer">
  <p className="hub-footer-note">
    Your chat history stays scoped to the selected assistant.
  </p>

  <div className="powered-by">
    <span>Powered by</span>
    <img
      src="/tmdal_logo.jpg"
      alt="Digital.ai Labs"
      className="powered-by-logo"
    />
    {/* <span className="powered-by-text">Digital.ai Labs</span> */}
  </div>

  <button
    type="button"
    className="hub-continue"
    disabled={!selectedBot || confirming}
    onClick={() => selectedBot && openBot(selectedBot)}
  >
    {confirming ? (
      <>
        <Loader2 className="hub-spinner" size={18} /> Opening…
      </>
    ) : (
      <>
        Continue <ArrowRight size={18} />
      </>
    )}
  </button>
</footer>
</div>
  );
}

export default ProfileSelection;
